/**
 * Trust Game スコアリング
 * 参考: Berg et al. (1995) "Trust, Reciprocity, and Social History"
 * 学習速度は「裏切り後の委託額調整量」で定義する（正値=削減、負値=増加）
 */

export interface TrustTrialRecord {
  trial: number;
  invested: number;             // プレイヤーが委託した額 (0–100)
  returned: number;             // 相手が返金した額
  charId: string;               // 相手キャラ ID
}

export interface TrustScore {
  totalTrials: number;
  avgInvestRate: number;        // 平均委託率 (0–1)
  avgReturn: number;            // 平均返金額
  learningSpeed: number | null; // 裏切り後の委託額削減幅（正 = 慎重になった）。裏切り 0 回は null
  totalNetGain: number;         // returned - invested の累計
}

/**
 * 裏切りの定義: returned < invested（預けた額より少なく返ってきた）
 * 学習速度: 裏切り直後の試行で委託額が下がった量の平均
 */
export function computeTrustScore(trials: TrustTrialRecord[]): TrustScore {
  const totalTrials = trials.length;
  if (totalTrials === 0) {
    return { totalTrials: 0, avgInvestRate: 0, avgReturn: 0, learningSpeed: 0, totalNetGain: 0 };
  }

  const avgInvestRate =
    trials.reduce((sum, t) => sum + t.invested, 0) / totalTrials / 100;

  const avgReturn = trials.reduce((sum, t) => sum + t.returned, 0) / totalTrials;

  // 裏切られた試行の直後に委託額がどれだけ減ったか
  const adjustments: number[] = [];
  for (let i = 0; i < trials.length - 1; i++) {
    const betrayed = trials[i].returned < trials[i].invested;
    if (betrayed) {
      const delta = trials[i].invested - trials[i + 1].invested;
      adjustments.push(delta);
    }
  }
  const learningSpeed =
    adjustments.length > 0
      ? adjustments.reduce((sum, d) => sum + d, 0) / adjustments.length
      : null;

  const totalNetGain = trials.reduce((sum, t) => sum + t.returned - t.invested, 0);

  return { totalTrials, avgInvestRate, avgReturn, learningSpeed, totalNetGain };
}
